// w30 Wedding is Coming — paper-invitation preview (heraldic mood).
// Outer iron-foil frame in deep stone, inner card uses bg/ink/accent.
// Eyebrow "TO'Y MAROSIMI" → heraldic crown medallion (or photo) →
// names in template font → date + venue caption.

import { Photo, type LayoutCtx } from "./_shared";

const STONE_GRADIENT =
  "linear-gradient(135deg, #1A1816 0%, #2B2521 28%, #3A312A 50%, #2B2521 72%, #0F0D0C 100%)";

export function WeddingIsComingPreviewLayout({
  names, date, venue, accent, ink, bg, font, photo,
}: LayoutCtx) {
  const captionDate = formatDate(date);
  return (
    <div style={{ position: "absolute", inset: 0, background: STONE_GRADIENT, padding: "5%", display: "flex" }}>
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55, inset 0 0 0 6px ${bg}, inset 0 0 0 7px ${accent}33`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TO&apos;Y MAROSIMI
        </div>

        {/* Heraldic crown medallion */}
        <div style={{ width: "60%", aspectRatio: "1/1", position: "relative", marginBottom: "5%" }}>
          <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: STONE_GRADIENT, padding: "5%" }}>
            <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: bg, padding: "4%", boxSizing: "border-box" }}>
              <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", position: "relative", border: `1px solid ${accent}88` }}>
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <svg viewBox="0 0 100 100" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
                      <defs>
                        <radialGradient id="w30-bg" cx="50%" cy="50%" r="60%">
                          <stop offset="0%" stopColor={accent} stopOpacity="0.35" />
                          <stop offset="100%" stopColor={bg} stopOpacity="0" />
                        </radialGradient>
                      </defs>
                      <rect width="100" height="100" fill="url(#w30-bg)" />
                      <g stroke={accent} strokeWidth="1" fill="none" strokeLinecap="round">
                        <path d="M22 64 L 30 38 L 38 56 L 50 30 L 62 56 L 70 38 L 78 64" />
                        <line x1="18" y1="64" x2="82" y2="64" />
                        <circle cx="30" cy="38" r="2" fill={accent} />
                        <circle cx="50" cy="30" r="2.6" fill={ink} stroke={ink} />
                        <circle cx="70" cy="38" r="2" fill={accent} />
                        <line x1="28" y1="72" x2="72" y2="72" strokeWidth="0.6" />
                      </g>
                      <text x="50" y="83" textAnchor="middle" fill={accent} fontFamily={font} fontSize="9" letterSpacing="0.2">ANNO</text>
                    </svg>
                  }
                />
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(180deg, transparent 55%, ${accent}22 100%)` }} />
              </div>
            </div>
          </div>
        </div>

        <div style={{ fontFamily: font, fontSize: 26, lineHeight: 1.05, color: ink, letterSpacing: "0.03em", textTransform: "uppercase" }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TO&apos;Y · {captionDate}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  return `${parseInt(m[1], 10)} ${months[parseInt(m[2], 10) - 1] ?? ""} ${m[3]}`;
}
